/**
 * Edit-feature dialog (LOCKER-408 DoD11, DoD12, DoD13).
 *
 * What changed against the version inlined in `FeaturePage.tsx`:
 *
 *  - the raw `<input type="checkbox" id="defaultActivatedCheckbox">` is a MUI
 *    `Switch`, the same control the rollout rows use, so the same gesture means
 *    the same thing on both surfaces (DoD12);
 *  - both actions are `Sl2Btn`, so they read in sentence case like every other
 *    button in the app instead of MUI's uppercase default (DoD12);
 *  - the read-only `refName` is an identifier in the HEADER, outside the
 *    editable field group — inside it, styled identically to the fields around
 *    it, it read as editable (DoD13);
 *  - the default-activated control states how many organizations gain or lose
 *    access if it is flipped. It is the highest-consequence field on the page
 *    and previously carried no explanation at all (DoD13);
 *  - the title and the refName caption came from `feature.editFeature` and
 *    `feature.refNameReadOnly`, which existed in neither locale file and
 *    rendered as raw keys (DoD11).
 */
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Feature } from "shared/feature"
import { readDefaultActivated } from "shared/featureResolution"
import { Sl2Btn } from "../Sl2Btn"
import { FeatureRolloutCounts } from "./featureRolloutModel"

const groupLabelSx = {
  fontSize: "0.6875rem",
  letterSpacing: "0.07em",
  textTransform: "uppercase" as const,
  fontWeight: 600,
  color: "text.secondary",
}

const CATEGORIES = ["Reservation", "Locker", "Other"] as const

type FeatureEditDialogProps = {
  open: boolean
  feature: Feature
  /** Live rollout counts, for the default-activated consequence copy. */
  counts: FeatureRolloutCounts
  isSaving: boolean
  /** Message from a rejected save, surfaced in the dialog rather than the console. */
  saveError?: string
  onClose: () => void
  onSubmit: (values: {
    name: string
    description: string
    category: string
    defaultActivated: boolean
  }) => void
}

/**
 * Who is affected if the default flips (LOCKER-408 DoD13).
 *
 * Turning it ON grants access to the organizations that are currently
 * UNASSIGNED — not to the ones explicitly deactivated, which keep their `off`
 * now that an assignment wins (DoD1). Turning it OFF revokes from exactly the
 * organizations currently granted by the default. Both numbers come from the
 * same counts the rollout panel renders, so the copy cannot disagree with the
 * bar on the page behind it.
 *
 * Four cases, not two: the copy has to distinguish the STORED state from an
 * unsaved flip. Keying it off the switch's current value alone produced
 * "Currently on. Turning it off…" next to a switch the operator had just moved
 * to on from a stored off — describing the opposite of what Save would do.
 */
const consequenceOf = (
  counts: FeatureRolloutCounts,
  storedDefault: boolean,
  pendingDefault: boolean,
): { key: string; count: number } => {
  if (pendingDefault === storedDefault) {
    return storedDefault
      ? { key: "feature.defaultStaysOn", count: counts.activatedByDefault }
      : { key: "feature.defaultStaysOff", count: counts.unassigned }
  }
  return pendingDefault
    ? { key: "feature.defaultWillTurnOn", count: counts.unassigned }
    : { key: "feature.defaultWillTurnOff", count: counts.activatedByDefault }
}

export const FeatureEditDialog: FC<FeatureEditDialogProps> = ({
  open,
  feature,
  counts,
  isSaving,
  saveError,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [category, setCategory] = useState("")
  const [defaultActivated, setDefaultActivated] = useState(false)

  // Re-seed from the feature each time the dialog opens, so a cancelled edit
  // does not leak into the next one.
  useEffect(() => {
    if (!open) return
    setName(feature?.name || "")
    setDescription(feature?.description || "")
    setCategory(feature?.category || "")
    setDefaultActivated(readDefaultActivated(feature))
  }, [open, feature])

  const storedDefault = readDefaultActivated(feature)
  const flipped = defaultActivated !== storedDefault
  const consequence = consequenceOf(counts, storedDefault, defaultActivated)

  return (
    <Dialog data-ui="UI-SL2W-275"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid="feature-edit-dialog"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          {t("feature.editFeature")}
        </Typography>
        {/* refName as an identifier, out of the form entirely (DoD13). */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: 0.75 }}
        >
          <Box
            component="span"
            data-testid="feature-edit-refname"
            sx={{
              fontFamily: "monospace",
              bgcolor: "grey.100",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.75rem",
              color: "rgba(0, 0, 0, 0.75)",
            }}
          >
            {feature?.refName}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t("feature.refNameReadOnly")}
          </Typography>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2.5} mt={1}>
          {saveError && <Alert severity="error">{saveError}</Alert>}

          <Typography sx={groupLabelSx}>{t("feature.groupDetails")}</Typography>
          <TextField
            label={t("feature.name")}
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>{t("feature.category")}</InputLabel>
            <Select
              value={category || ""}
              label={t("feature.category")}
              onChange={(e) => setCategory(e.target.value as string)}
            >
              <MenuItem value="">
                <em>-</em>
              </MenuItem>
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(`feature.category.${c}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t("feature.description")}
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />

          <Divider />

          <Typography sx={groupLabelSx}>{t("feature.groupRollout")}</Typography>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{
              border: "1px solid",
              borderColor: flipped ? "#ed6c02" : "rgba(0, 0, 0, 0.12)",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Switch
              checked={defaultActivated}
              onChange={(e) => setDefaultActivated(e.target.checked)}
              inputProps={
                {
                  "aria-label": t("feature.defaultActivated") as string,
                  "data-testid": "feature-default-activated-switch",
                } as any
              }
              sx={{
                mt: -0.5,
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#234285" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#234285",
                },
              }}
            />
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {t("feature.defaultActivatedForAll")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="feature-default-consequence"
              >
                {t(consequence.key, { count: consequence.count })}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Sl2Btn variant="outlined" onClick={onClose} disabled={isSaving}>
          {t("cancel")}
        </Sl2Btn>
        <Sl2Btn
          variant="contained"
          onClick={() =>
            onSubmit({ name, description, category, defaultActivated })
          }
          disabled={isSaving}
          data-testid="feature-edit-save"
          sx={{ bgcolor: "#234285" }}
        >
          {isSaving ? t("saving") : t("save")}
        </Sl2Btn>
      </DialogActions>
    </Dialog>
  )
}

export default FeatureEditDialog
